/**
 * DATA-18: Cohort Tab — Retention matrix heatmap.
 * Rows: cohort_week, Columns: period (0~12).
 * Cell: retained_users / total_users as percentage, color-coded by retention rate.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchCohortRetention } from "../../api/admin-segmentation";
import type { CohortRetentionRow } from "../../api/admin-segmentation";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { defaultDateRange, fmt, pct } from "./helpers";

// ─── Constants ───

const MAX_PERIOD = 12;
const PERIODS = Array.from({ length: MAX_PERIOD + 1 }, (_, i) => i);

export function CohortTab(): React.JSX.Element {
  const { startDate, endDate, setStartDate, setEndDate } = useDateRange();
  const { data, error, loading, load } = useCohortData();

  useEffect(() => {
    void load(startDate, endDate);
  }, [load, startDate, endDate]);

  return (
    <div>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
        loading={loading}
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && <SummaryCards data={data} />}
      {data && <RetentionMatrix data={data} />}
    </div>
  );
}

// ─── Summary Cards ───

function SummaryCards({
  data,
}: {
  data: CohortMatrix;
}): React.JSX.Element {
  const cohortCount = data.cohorts.length;
  const totalUsers = data.cohorts.reduce((s, c) => s + c.totalUsers, 0);
  const avgWeek1 = computeAvgRetention(data.cohorts, 1);
  const avgWeek4 = computeAvgRetention(data.cohorts, 4);

  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <Card label={t("cohort.cohorts")} value={String(cohortCount)} />
        <Card label={t("cohort.total_users")} value={fmt(totalUsers)} />
        <Card label={t("cohort.avg_week1_retention")} value={pct(avgWeek1)} />
        <Card label={t("cohort.avg_week4_retention")} value={pct(avgWeek4)} />
      </div>
    </section>
  );
}

function Card({
  label,
  value,
}: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 20 }}>
      <span style={s.cardLabel}>{label}</span>
      <span style={shared.bigNumber}>{value}</span>
    </div>
  );
}

function computeAvgRetention(
  cohorts: ReadonlyArray<CohortInfo>,
  period: number,
): number {
  const rates = cohorts
    .map((c) => c.periods.get(period))
    .filter((r): r is number => r !== undefined);
  if (rates.length === 0) return 0;
  return rates.reduce((s, r) => s + r, 0) / rates.length;
}

// ─── Retention Matrix ───

function RetentionMatrix({
  data,
}: {
  data: CohortMatrix;
}): React.JSX.Element {
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("cohort.retention_matrix")}</h3>
      <div style={s.tableWrap}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <Th>{t("cohort.col_cohort_week")}</Th>
              <Th>{t("cohort.col_users")}</Th>
              {PERIODS.map((p) => (
                <Th key={p}>W{p}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.cohorts.map((cohort) => (
              <tr key={cohort.week}>
                <Td>{cohort.week}</Td>
                <Td mono>{fmt(cohort.totalUsers)}</Td>
                {PERIODS.map((p) => (
                  <RetentionCell key={p} rate={cohort.periods.get(p)} />
                ))}
              </tr>
            ))}
            {data.cohorts.length === 0 && (
              <tr>
                <Td colSpan={MAX_PERIOD + 3}>{t("cohort.no_cohort_data")}</Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RetentionCell({
  rate,
}: { rate: number | undefined }): React.JSX.Element {
  if (rate === undefined) {
    return <td style={s.emptyCell} />;
  }

  const bg = retentionColor(rate);
  const text = rate >= 0.5 ? "#fff" : "var(--color-text)";

  return (
    <td style={{ ...s.heatCell, backgroundColor: bg, color: text }}>
      {(rate * 100).toFixed(0)}%
    </td>
  );
}

function retentionColor(rate: number): string {
  if (rate >= 0.8) return "#059669";
  if (rate >= 0.6) return "#10b981";
  if (rate >= 0.4) return "#34d399";
  if (rate >= 0.2) return "#a7f3d0";
  if (rate > 0) return "#d1fae5";
  return "#f3f4f6";
}

// ─── Date Range Picker ───

function DateRange({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  loading,
}: {
  startDate: string;
  endDate: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
  loading: boolean;
}): React.JSX.Element {
  return (
    <div style={s.dateRow}>
      <label style={s.dateLabel}>
        From
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          style={s.dateInput}
        />
      </label>
      <label style={s.dateLabel}>
        To
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          style={s.dateInput}
        />
      </label>
      {loading && <span style={shared.loadingText}>{t("common.loading")}</span>}
    </div>
  );
}

// ─── Data Transformation ───

type CohortInfo = {
  readonly week: string;
  readonly totalUsers: number;
  readonly periods: Map<number, number>;
};

type CohortMatrix = {
  readonly cohorts: ReadonlyArray<CohortInfo>;
};

function buildMatrix(rows: ReadonlyArray<CohortRetentionRow>): CohortMatrix {
  const map = new Map<string, CohortInfo>();

  for (const row of rows) {
    const existing = map.get(row.cohort_week);
    if (existing) {
      existing.periods.set(row.period, row.retention_rate);
    } else {
      const periods = new Map<number, number>();
      periods.set(row.period, row.retention_rate);
      map.set(row.cohort_week, {
        week: row.cohort_week,
        totalUsers: row.total_users,
        periods,
      });
    }
  }

  const cohorts = Array.from(map.values()).sort((a, b) =>
    b.week.localeCompare(a.week),
  );
  return { cohorts };
}

// ─── Hooks ───

function useDateRange(): {
  startDate: string;
  endDate: string;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
} {
  const defaults = defaultDateRange(90);
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  return { startDate, endDate, setStartDate, setEndDate };
}

function useCohortData(): {
  data: CohortMatrix | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<CohortMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchCohortRetention({
          start_date: start,
          end_date: end,
        });
        setData(buildMatrix(rows));
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
  tableWrap: { overflowX: "auto" },
  heatCell: {
    padding: "6px 8px",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    borderBottom: "1px solid var(--color-border)",
  },
  emptyCell: {
    padding: "6px 8px",
    borderBottom: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  dateRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 24,
  },
  dateLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
  },
};
